import { ReactElement, createContext, useContext, useMemo } from "react";

import { useGetUsers } from "@/requests/user.queries";
import { DriverType } from "@/types/driver.types";
import { EXTERNAL_DRIVER } from "@/types/global";
import { DriverContext } from "@/types/providers";
import { UserType } from "@/types/user.types";

const context = createContext<DriverContext>(null);

export const DriversContextProvider = ({
    children,
    withExternal = true,
}: {
    children: ReactElement;
    withExternal?: boolean;
}) => {
    const { data } = useGetUsers({ hideManagers: false });
    const allUsers = useMemo(
        () => (data ? data.filter((item) => item.userType).sort((a, b) => a.userType - b.userType) : []),
        [data]
    );
    const drivers = useMemo(
        () => [
            ...(withExternal ? [EXTERNAL_DRIVER] : []),
            ...allUsers
                .filter((item) => !item.isDeleted && item.name && item.driverId && item.userType === UserType.DRIVER)
                .map(
                    ({ vehicleData, driverId, isDeleted, n2MobileName, dbId, id }): DriverType => ({
                        id: driverId as string,
                        name: n2MobileName || "",
                        isDeleted,
                        dbId,
                        userId: id,
                        vehicle: {
                            brand: vehicleData?.brand || "",
                            licensePlate: vehicleData?.licensePlate || "",
                            id: vehicleData?.id || "",
                            model: vehicleData?.model || "",
                        },
                    })
                )
                .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
        ],
        [allUsers, withExternal]
    );

    const parsedDrivers = useMemo(
        () =>
            drivers.reduce<Record<string, DriverType>>((prev, curr) => {
                if (curr.id && !curr.isDeleted) prev[curr.id] = curr;
                return prev;
            }, {}),
        [drivers]
    );

    const getDriverByVehicleId = (vehicleId: string) => {
        return drivers.find((user) => user.vehicle && user.vehicle.id === vehicleId);
    };

    return (
        <context.Provider
            value={{
                drivers: drivers || [],
                parsedDrivers,
                allDrivers: allUsers || [],
                getDriverByVehicleId,
            }}
        >
            {children}
        </context.Provider>
    );
};

const useDriversContext = () => {
    const ctx = useContext(context);
    if (!ctx) {
        throw new Error("useDriversContext must be used within a DriversContextProvider");
    } else {
        return ctx;
    }
};

export default useDriversContext;
